import { useEffect, useState, useRef } from "react";
import { api, apiError } from "@/lib/apiClient";
import { useWorkspace } from "@/context/WorkspaceContext";
import useScopeGuard from "@/hooks/useScopeGuard";
import { PageHeader, StatCard } from "@/components/PageBits";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Gauge, Clock, Mic, Video, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const PLAN_NAMES = ["FREE", "STARTER", "PRO", "BUSINESS", "ENTERPRISE"];

function fmt(v) {
  return v < 0 ? "∞" : v;
}

function invoiceAmount(invoice) {
  if (!Number.isFinite(invoice.total) || !invoice.currency) return "—";
  const currency = invoice.currency.toUpperCase();
  const formatter = new Intl.NumberFormat("fr-FR", { style: "currency", currency });
  // Stripe keeps ISK and UGX in hundredths for compatibility (docs.stripe.com/currencies).
  const digits = ["ISK", "UGX"].includes(currency) ? 2 : formatter.resolvedOptions().maximumFractionDigits;
  return formatter.format(invoice.total / 10 ** digits);
}

export default function Plan() {
  const { orgId, currentOrg, loading } = useWorkspace();
  const isCurrent = useScopeGuard(orgId);
  const [billing, setBilling] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [moreInvoices, setMoreInvoices] = useState(false);
  const activeOrg = useRef(orgId); activeOrg.current = orgId;
  const [plan, setPlan] = useState(null);
  const [quota, setQuota] = useState(null);
  const [selected, setSelected] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setPlan(null); setQuota(null); setBilling(null); setInvoices([]); setMoreInvoices(false); setSelected("");
    if (!orgId) return;
    api.get(`/orgs/${orgId}/plan`).then(({ data }) => { if (!cancelled) { setPlan(data); setSelected(data.plan); } }).catch(() => {});
    api.get(`/orgs/${orgId}/billing`).then(({ data }) => { if (!cancelled) setBilling(data); }).catch(() => {});
    if (currentOrg?.role === "owner") api.get(`/orgs/${orgId}/billing/invoices`).then(({ data }) => { if (!cancelled) { setInvoices(data.items); setMoreInvoices(data.has_more); } }).catch(() => {});
    api.get(`/orgs/${orgId}/quota`).then(({ data }) => { if (!cancelled) setQuota(data); }).catch(() => {});
    return () => { cancelled = true; };
  }, [orgId, currentOrg?.role]);

  const upgrade = async () => {
    if (!orgId || loading || !selected) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/orgs/${orgId}/billing/checkout`, { plan: selected });
      if (isCurrent()) window.location.assign(data.url);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const limits = plan?.limits || {};
  const usage = quota?.usage || {};

  return (
    <div data-testid="plan-page">
      <PageHeader
        title="Plan & Usage"
        description={`Contract, quotas and metered consumption for ${currentOrg?.name || "your org"}.`}
        actions={
          <div className="flex items-center gap-2">
            <Select value={selected} onValueChange={setSelected}>
              <SelectTrigger data-testid="plan-select" className="w-40 bg-zinc-950 border-zinc-800"><SelectValue /></SelectTrigger>
              <SelectContent className="border-zinc-800 bg-zinc-950">
                {PLAN_NAMES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button data-testid="apply-plan" disabled={!orgId || loading || !billing?.enabled || currentOrg?.role !== "owner" || !billing?.plans?.includes(selected) || billing?.has_subscription || busy || selected === plan?.plan} onClick={upgrade}
              className="bg-zinc-50 text-zinc-900 hover:bg-zinc-200">Souscrire</Button>
          </div>
        }
      />

      {!billing?.enabled && <p className="mb-4 text-sm text-zinc-400">La souscription en ligne n’est pas configurée. Contactez la plateforme pour modifier votre contrat.</p>}
      {billing?.enabled && currentOrg?.role === "owner" && <Button className="mb-4" variant="outline" onClick={async () => { try { const { data } = await api.post(`/orgs/${orgId}/billing/portal`); if (isCurrent()) window.location.assign(data.url); } catch (err) { toast.error(apiError(err)); } }}>Gérer abonnement et paiement</Button>}
      {invoices.length > 0 && <ul className="mb-5 space-y-2">{invoices.map((invoice) => <li key={invoice.id} className="text-sm">{invoice.number || invoice.id} · {invoiceAmount(invoice)} · {invoice.status} {invoice.hosted_url && <a className="underline" href={invoice.hosted_url} target="_blank" rel="noreferrer">Consulter la facture</a>}</li>)}</ul>}
      {moreInvoices && <Button variant="outline" disabled={busy} onClick={async () => { setBusy(true); try { const { data } = await api.get(`/orgs/${orgId}/billing/invoices`, { params: { starting_after: invoices.at(-1).id } }); if (activeOrg.current === orgId) { setInvoices((items) => [...items, ...data.items]); setMoreInvoices(data.has_more); } } catch (err) { if (activeOrg.current === orgId) toast.error(apiError(err)); } finally { setBusy(false); } }}>Factures précédentes</Button>}
      <div className="mb-6 flex items-center gap-3 rounded-md border border-zinc-800 bg-zinc-900/30 p-5">
        <Gauge className="h-5 w-5 text-zinc-400" />
        <div>
          <div className="text-xs uppercase tracking-wider text-zinc-500">Current plan</div>
          <div className="font-mono text-2xl font-semibold text-zinc-50" data-testid="current-plan">{plan?.plan || "—"}</div>
        </div>
      </div>

      {quota?.alerts?.length > 0 && (
        <div className="mb-6 space-y-2" data-testid="quota-alerts">
          {quota.alerts.map((a, i) => (
            <div key={i} className={`flex items-center gap-2 rounded-md border px-4 py-2 text-sm ${a.status === "exceeded" ? "border-red-900/50 bg-red-950/30 text-red-300" : "border-amber-900/50 bg-amber-950/20 text-amber-300"}`}>
              <AlertTriangle className="h-4 w-4" /> {a.metric}: {a.used}/{a.limit} ({a.status})
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard testId="limit-participants" label="Max participants" value={fmt(limits.max_participants ?? 0)} />
        <StatCard testId="limit-sessions" label="Concurrent sessions" value={fmt(limits.max_concurrent_sessions ?? 0)} hint={`${usage.active_sessions ?? 0} active now`} />
        <StatCard testId="usage-video-hours" label="Video hrs / week" value={usage.weekly_video_hours ?? 0} hint={`limit ${fmt(limits.weekly_video_hours ?? 0)}`} icon={Clock} />
        <StatCard testId="limit-storage" label="Storage (GB)" value={fmt(limits.storage_gb ?? 0)} />
        <StatCard testId="usage-audio-min" label="Audio min (7d)" value={usage.audio_minutes ?? 0} hint={`limit ${fmt(limits.audio_minutes ?? 0)}`} icon={Mic} />
        <StatCard testId="usage-video-min" label="Video min (7d)" value={usage.video_minutes ?? 0} hint={`limit ${fmt(limits.video_minutes ?? 0)}`} icon={Video} />
        <StatCard testId="usage-pm" label="Participant min (7d)" value={usage.participant_minutes ?? 0} hint={`limit ${fmt(limits.participant_minutes ?? 0)}`} />
        <StatCard testId="limit-retention" label="Retention (days)" value={fmt(limits.retention_days ?? 0)} />
      </div>

      <div className="mt-6 rounded-md border border-zinc-800 bg-zinc-900/30 p-5">
        <h2 className="mb-3 text-sm font-medium text-zinc-200">Included features</h2>
        <div className="flex flex-wrap gap-2" data-testid="plan-features">
          {(limits.features || []).map((f) => (
            <span key={f} className="inline-flex items-center gap-1.5 rounded border border-zinc-800 px-2 py-1 font-mono text-xs text-emerald-400">
              {plan?.available_features?.includes(f) ? <CheckCircle2 className="h-3.5 w-3.5" /> : <AlertTriangle className="h-3.5 w-3.5 text-amber-400" />} {f}{!plan?.available_features?.includes(f) && " · indisponible"}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
